"use client";

import React from 'react';
import Link from 'next/link';
import { useStore } from '@/context/StoreContext';
import { getStoreOpenStatus } from '@/lib/formatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTiktok, faWhatsapp } from '@fortawesome/free-brands-svg-icons';

export default function Footer() {
    const { settings } = useStore();
    const storeName = settings.store_name || "Loja Virtual";
    const openStatus = getStoreOpenStatus(settings.horario_funcionamento);

    // Endereço físico formatado
    const hasAddress = settings.street || settings.city;
    const formattedAddress = [
        settings.street ? `${settings.street}${settings.number ? ', ' + settings.number : ''}` : '',
        settings.complement,
        settings.district,
        settings.city && settings.state ? `${settings.city}/${settings.state}` : (settings.city || settings.state),
        settings.cep ? `CEP: ${settings.cep}` : ''
    ].filter(Boolean).join(' - ');

    return (
        <footer className="bg-white border-t border-gray-200 pt-10 pb-20 md:pb-10">
            <div className="container mx-auto px-4 text-center md:text-left">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* COLUNA 1: SOBRE & REDES */}
                    <div>
                        {settings.logo_url ? (
                            <img src={settings.logo_url} alt={storeName} className="h-9 w-auto object-contain mb-2 mx-auto md:mx-0" />
                        ) : (
                            <h4 className="font-extrabold text-lg text-[var(--text-main,#111827)] mb-1">{storeName}</h4>
                        )}
                        {settings.slogan && (
                            <p className="text-xs text-[var(--primary)] font-semibold mb-3 tracking-wide">{settings.slogan}</p>
                        )}
                        <h4 className="font-bold text-gray-900 mb-2 text-sm uppercase tracking-wider">Sobre a Loja</h4>
                        <p className="text-sm text-gray-500 leading-relaxed">{settings.about || `Bem-vindo à ${storeName}.`}</p>
                        {settings.cnpj && <p className="text-xs text-gray-400 mt-2 font-mono">CNPJ: {settings.cnpj}</p>}

                        {/* Redes Sociais */}
                        {(settings.instagram_url || settings.facebook_url || settings.tiktok_url) && (
                            <div className="flex items-center justify-center md:justify-start gap-3 mt-4">
                                {settings.instagram_url && (
                                    <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-100 hover:bg-pink-50 hover:text-pink-600 text-gray-600 flex items-center justify-center transition-all" title="Instagram">
                                        <FontAwesomeIcon icon={faInstagram} className="text-sm" />
                                    </a>
                                )}
                                {settings.facebook_url && (
                                    <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-600 flex items-center justify-center transition-all" title="Facebook">
                                        <FontAwesomeIcon icon={faFacebook} className="text-sm" />
                                    </a>
                                )}
                                {settings.tiktok_url && (
                                    <a href={settings.tiktok_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gray-100 hover:bg-black hover:text-white text-gray-600 flex items-center justify-center transition-all" title="TikTok">
                                        <FontAwesomeIcon icon={faTiktok} className="text-sm" />
                                    </a>
                                )}
                            </div>
                        )}
                    </div>

                    {/* COLUNA 2: ATENDIMENTO */}
                    <div>
                        <h4 className="font-bold text-gray-900 mb-4">Atendimento</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li><Link href="/minha-conta" className="hover:text-[var(--primary)] font-medium">Meus Pedidos</Link></li>
                            <li><Link href="/politicas?tab=trocas" className="hover:text-[var(--primary)]">Trocas & Devoluções</Link></li>
                            <li><Link href="/politicas?tab=entregas" className="hover:text-[var(--primary)]">Prazos & Entregas</Link></li>
                            <li><Link href="/politicas?tab=privacidade" className="hover:text-[var(--primary)]">Privacidade & Termos (LGPD)</Link></li>
                            <li><span className="text-gray-400 cursor-default">Compra 100% Segura & Criptografada</span></li>
                        </ul>
                    </div>

                    {/* COLUNA 3: CONTATO E ENDEREÇO */}
                    <div className="flex flex-col items-center md:items-start">
                        <h4 className="font-bold text-gray-900 mb-4">Contato & Localização</h4>
                        <div className="text-sm text-gray-600 space-y-2">
                            {settings.phone && (
                                <p className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faWhatsapp} className="text-emerald-500" />
                                    {settings.phone}
                                </p>
                            )}
                            {settings.email && (
                                <p className="text-gray-600">
                                    E-mail: <a href={`mailto:${settings.email}`} className="hover:underline">{settings.email}</a>
                                </p>
                            )}

                            {openStatus && (
                                <div className="pt-1 text-xs flex items-center gap-2">
                                    <FontAwesomeIcon icon={faClock} className={openStatus.isOpen ? "text-emerald-500" : "text-gray-400"} />
                                    <span className={openStatus.isOpen ? "text-emerald-700 font-semibold" : "text-gray-500 font-medium"}>
                                        {openStatus.label}
                                    </span>
                                </div>
                            )}

                            {hasAddress && (
                                <div className="pt-2 text-xs text-gray-500 flex items-start gap-2 max-w-xs">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 mt-1 flex-shrink-0" />
                                    <span>{formattedAddress}</span>
                                </div>
                            )}

                            {!settings.phone && !settings.email && !hasAddress && !openStatus && (
                                <p className="text-gray-400">Dúvidas? Fale conosco.</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-10 pt-6 border-t border-gray-100 text-center">
                    <p className="text-xs text-gray-400">Powered by <a href="https://aesirerp.com" target="_blank" rel="noopener noreferrer" className="font-bold hover:text-[var(--primary)]">AesirShop</a></p>
                </div>
            </div>
        </footer>
    );
}